import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useSelector } from 'react-redux';
import { convertObjectKeysToCamelCase } from 'common-package';
import { get, omit } from 'lodash-es';
import { klona } from 'klona';
import { Decimal } from 'decimal.js';
import { Switch } from 'antd-mobile';
import lookie from 'lookie';
import { useGetState, useLatest, useMemoizedFn, useMount, useUnmount } from 'ahooks';
import { SocketClientType } from '@/service/socketio';
import { MASSAGE_SETTING_CACHE, ShockwaveType } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import service from '@/service';
import { selectCurrentServingOrder } from '@/store/service';
import { ScrollView, Stepper, Toast } from '@/components';
import { genMinMaxSteps } from '@/components/stepper';
import { controItemExtranDom } from '@/pages/service/massage-setting/utils';

import cx from './index.module.scss';

interface Props {
  socketInstance: React.MutableRefObject<SocketClientType>;
  onInitOK: VoidFunc;
}

export interface DrawLineSettingBoxRefAttrs {
  getValues: () => Record<string, number>[];
}

// 自定义画线方案设置组件
const DrawLineSettingBox = forwardRef<DrawLineSettingBoxRefAttrs, Props>(({ socketInstance, onInitOK }, ref) => {
  const [isOpenManualAssist, setIsOpenManualAssist, getIsOpenManualAssist] = useGetState(true);
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [noduleLength, setModuleLength] = useState(0);
  const [massageSettingsOptions, setMassageSettingsOptions] = useState<GetMassageSettingsOptionsResponse>(null);
  const latestOrder = useLatest(currentServingOrder);
  const [settingValues, setSettingValues, getSettingValues] = useGetState<
    Partial<Record<Massage.ControlItemOptions, number>>
  >({
    'robot.force': 60,
    'robot.velocity': 10,
    'robot.height': 100,
    'shockwave.power': 30,
    'shockwave.frequency': 10,
    'shockwave.basic_hits': 2500,
    'shockwave.nodule_hits': 300,
    'radio_frequency.power': 10,
    'radio_frequency.duration': 30,
    /** 热砭石 */
    'hot_stone.duration': 30,
    // /** 矩阵式艾灸默认数据 */
    'moxibustion.acupoint_area': 1,
    'moxibustion.tech': 1,
    'moxibustion.duration': 5,
    'hot_stone.power': 15,
    'hot_stone.temperature_level': 1,
    'hot_stone.mode': 1,
  });
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const shouldClearSettingCache = useRef(false);

  // 生成人工单默认参数
  const generateDefaultManualSettings = useMemoizedFn(() => {
    return massageSettingsOptions
      ?.find(item => item.massageType === 'ManualMassage')
      ?.controlItemConfigs?.reduce((accur, item2) => {
        switch (item2.name) {
          case 'shockwave.power':
            accur['power'] = +item2.default.value;
            break;
          case 'radio_frequency.power':
            accur['power'] = new Decimal(item2.default.value || 0).div(100).toNumber();
            break;
          case 'shockwave.basic_hits':
            accur['hits'] = 2000;
            break;
          case 'shockwave.frequency':
            accur['frequency'] = +item2.default.value;
            break;
          case 'radio_frequency.duration':
            accur['duration'] = 60;
            break;
          case 'hot_stone.duration':
            accur['duration'] = +item2.default.value;
            break;
        }
        return accur;
      }, {});
  });

  const initData = useMemoizedFn(async () => {
    if (!currentServingOrder?.orderId) return;

    const backupSetting = klona(settingValues);

    try {
      const palpationData = await socketInstance.current.getPalpationData();
      const noduleData = palpationData?.palpation_data?.nodule_set;
      setModuleLength((noduleData?.left?.length || 0) + (noduleData?.right?.length || 0));

      // 获取用户设置的按摩方案并还原,通过接口数据设置默认值
      const { data: options } = await service.massage.getMassageSettingsOptions(currentServingOrder?.orderId);

      const formatOptions = convertObjectKeysToCamelCase(options);

      setMassageSettingsOptions(formatOptions);

      formatOptions
        ?.find(item => item.massageType === 'Massage')
        ?.controlItemConfigs?.forEach(item2 => {
          const primaryValue = item2.default?.value as any;
          backupSetting[item2.name] = primaryValue || primaryValue === 0 ? +primaryValue : null;
        });

      setTimeout(() => onInitOK?.(), 300);
    } catch (e) {
      console.error('error', e);
    } finally {
      setTimeout(() => scrollViewRef.current?.handleRefresh(), 200);
    }

    // 回显本地保存的数据
    try {
      const storageCache = lookie.get(MASSAGE_SETTING_CACHE) as {
        [orderId: string]: typeof settingValues & {
          isOpenManualAssist: boolean;
        };
      };
      const storageData = storageCache?.[latestOrder?.current?.orderId];

      if (storageData) {
        setSettingValues(omit(storageData, 'isOpenManualAssist'));
        setIsOpenManualAssist(get(storageData, 'isOpenManualAssist'));
      } else {
        // 本地无缓存，取接口默认值
        setSettingValues(backupSetting);
      }
    } catch (e) {
      console.log('回显数据失败', e);
    }
  });

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => {
        const reqParams = [];

        reqParams.push({
          force: settingValues['robot.force'],
          power: settingValues['shockwave.power'],
          frequency: settingValues['shockwave.frequency'],
          basic_hits: settingValues['shockwave.basic_hits'],
          ...(noduleLength ? { nodule_hits: settingValues['shockwave.nodule_hits'] } : {}),
        });

        getIsOpenManualAssist() && reqParams.push(generateDefaultManualSettings());
        return reqParams;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingValues]
  );

  useUnmount(() => {
    if (shouldClearSettingCache.current) {
      lookie.remove(MASSAGE_SETTING_CACHE);
    } else {
      lookie.set(MASSAGE_SETTING_CACHE, {
        [latestOrder.current?.orderId]: {
          isOpenManualAssist: getIsOpenManualAssist(),
          ...getSettingValues(),
        },
      });
    }
  });

  useEffect(() => {
    if (currentServingOrder?.orderId) {
      initData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId]);

  return (
    <div className={cx('DrawLineSettingBox')}>
      <div className={cx('manual-assist-wrapper')}>
        <div className={cx('manual-assist')}>
          <div className={cx('label')}>人工辅助治疗</div>
          <div className={cx('tips')}>
            <Icon name="tips" className={cx('tips-icon')} />
            <span>开启后，机器理疗结束后将进行人工辅助理疗</span>
          </div>
          <Switch
            onChange={setIsOpenManualAssist}
            checked={isOpenManualAssist}
            style={{
              '--width': '48px',
              '--height': '28px',
              '--border-width': '2px',
            }}
          />
        </div>
      </div>
      <ScrollView className={cx('setting-box-wrapper')} ref={scrollViewRef}>
        <div className={cx('setting-box')}>
          {massageSettingsOptions
            ?.find(item => item.massageType === 'Massage')
            ?.controlItemConfigs.filter(item => !!controItemExtranDom[item.name]?.label)
            // 是否展示痛点设置取决于是否有标记痛点
            ?.filter(item => (item.name === 'shockwave.nodule_hits' ? !!noduleLength : true))
            ?.map(item => (
              <div key={item.name} className={cx('setting-item')}>
                <div className={cx('label')}>{controItemExtranDom[item.name]?.label}</div>
                <div className={cx('setter')}>
                  <Stepper
                    value={settingValues[item.name]}
                    {...genMinMaxSteps(item.values)}
                    onChange={value => {
                      if (
                        item.name === 'shockwave.frequency' &&
                        value >= 12 &&
                        currentServingOrder?.shockwaveType === ShockwaveType['DEJI']
                      ) {
                        Toast.show('超过12Hz理疗渗透力会降低哦');
                      }
                      setSettingValues(preValue => {
                        const clonedValue = klona(preValue);
                        clonedValue[item.name] = value;
                        return clonedValue;
                      });
                    }}
                    suffix={controItemExtranDom[item.name]?.suffix}
                  />
                </div>
              </div>
            ))}
        </div>
      </ScrollView>
    </div>
  );
});

export default DrawLineSettingBox;
